import {
    AuthContext,
    SidebarNav,
    SidebarNavFooter,
    SidebarNavGroup,
    SidebarNavLink,
    isDemoModeActive,
} from '@enter/core';
import {
    mdiAccountNetworkOutline,
    mdiCancel,
    mdiCardAccountDetailsOutline,
    mdiCashMultiple,
    mdiChartLine,
    mdiCheckCircleOutline,
    mdiClipboardCheckOutline,
    mdiClipboardTextOutline,
    mdiCodeBraces,
    mdiCogOutline,
    mdiCreationOutline,
    mdiEmailOutline,
    mdiEyeOffOutline,
    mdiEyeOutline,
    mdiFileCabinet,
    mdiFileTreeOutline,
    mdiHandshakeOutline,
    mdiOfficeBuildingOutline,
    mdiOpenInNew,
    mdiReceiptTextOutline,
    mdiRobotOutline,
} from '@mdi/js';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMatch, useParams } from 'react-router-dom';
import { IInjectedRootStore } from 'stores';
import * as S from './main.sidebar.styles';
type IMainSidebarProps = IInjectedRootStore;

export const MainSidebar: React.FunctionComponent<IMainSidebarProps> = inject('rootStore')(
    observer((props: IMainSidebarProps): React.ReactElement => {
        const { rootStore } = props;
        const { messageStore } = rootStore;
        const { refreshOpenInboxCount, openInboxTotal } = messageStore;
        const context = useContext(AuthContext);
        const params = useParams();
        const accountId = params.accountId;

        const route = (path: string): string => (accountId ? `/${accountId}/${path}` : path);
        const isPatientCollectionsRoute = useMatch(route('patient-collections/*'));
        const isSettingsRoute = useMatch(route('settings/*'));

        const [showUniversityPassword, setShowUniversityPassword] = useState(false);
        const [copied, setCopied] = useState(false);
        const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        const universityPassword = 'entertheuni';
        const copyUniversityPassword = (): void => {
            try {
                if (navigator?.clipboard?.writeText) {
                    navigator.clipboard.writeText(universityPassword);
                    setCopied(true);
                    if (copyTimeoutRef.current) {
                        clearTimeout(copyTimeoutRef.current);
                    }
                    copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
                    return;
                }
                const textarea = document.createElement('textarea');
                textarea.value = universityPassword;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                setCopied(true);
                if (copyTimeoutRef.current) {
                    clearTimeout(copyTimeoutRef.current);
                }
                copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
            } catch {
                // ignore copy failures
            }
        };

        useEffect(
            () => () => {
                if (copyTimeoutRef.current) {
                    clearTimeout(copyTimeoutRef.current);
                }
            },
            [],
        );

        // NB: setup background poll to check for messages
        // Initial call on mount to show count immediately
        // Background poll updates the count every 3 minutes
        useEffect(() => {
            try {
                context.cancelGroup && context.cancelGroup('messaging');
                context.setGroup && context.setGroup('messaging');
                refreshOpenInboxCount();
                const updateInterval = 3 * 60 * 1000;
                const interval = setInterval(refreshOpenInboxCount, updateInterval);
                return (): void => clearInterval(interval);
            } catch (err) {
                // bury exceptions
            }
        }, [context, refreshOpenInboxCount]);

        const demoModeEnabled = isDemoModeActive();
        const { layoutStore } = rootStore;
        const rail = layoutStore.sidebarCollapsed;

        return (
            <S.SideBar rail visible={!rail} onClose={layoutStore.collapseSidebar}>
                <SidebarNav aria-label="Main">
                    <SidebarNavGroup rail={rail} label="Tasks">
                        <SidebarNavLink
                            rail={rail}
                            to={route('messaging')}
                            icon={mdiCheckCircleOutline}
                            label="Tasks"
                            count={openInboxTotal}
                        />
                        <SidebarNavLink rail={rail} to={route('reports')} icon={mdiChartLine} label="Reports" />
                    </SidebarNavGroup>
                    <SidebarNavGroup rail={rail} label="Revenue Cycle">
                        <SidebarNavLink
                            rail={rail}
                            to={route('claims')}
                            icon={mdiClipboardTextOutline}
                            label="Claims"
                        />
                        <SidebarNavLink
                            rail={rail}
                            to={route('denials')}
                            icon={mdiCancel}
                            label="Denials & Rejections"
                        />
                        <SidebarNavLink rail={rail} to={route('remits')} icon={mdiReceiptTextOutline} label="Remits" />
                        <SidebarNavLink
                            rail={rail}
                            to={route('patient-collections/needs')}
                            icon={mdiCashMultiple}
                            label="Patient Collections"
                            active={!!isPatientCollectionsRoute}
                        />
                        <SidebarNavLink
                            rail={rail}
                            to={route('eligibility')}
                            icon={mdiClipboardCheckOutline}
                            label="Eligibility"
                        />
                    </SidebarNavGroup>
                    {/*
                     * Payers is NOT in the File Cabinet. It shipped there because Jordan's SUPPORT design
                     * puts it beside the other reference data. That inference was wrong for this app: a
                     * provider opens Payers to answer "where did my claim go", which is a question about
                     * their claims, not a lookup table they browse. John Russell and Aleksandr Tynianov
                     * both went looking for it at the top level and could not find it.
                     *
                     * Payers Providers stays here until it folds into the Payers page, as the design intends.
                     */}
                    <SidebarNavGroup rail={rail} label="Your Practice">
                        <SidebarNavLink
                            rail={rail}
                            to={route('payers')}
                            icon={mdiOfficeBuildingOutline}
                            label="Payers"
                        />
                        <SidebarNavLink
                            rail={rail}
                            to={route('payers-providers')}
                            icon={mdiAccountNetworkOutline}
                            label="Payers Providers"
                        />
                        <SidebarNavLink
                            rail={rail}
                            to={route('chargemasters')}
                            icon={mdiHandshakeOutline}
                            label="Contracts"
                        />
                        <SidebarNavLink
                            rail={rail}
                            to={route('roster')}
                            icon={mdiCardAccountDetailsOutline}
                            label="Rosters & Facilities"
                        />
                    </SidebarNavGroup>
                    <SidebarNavGroup rail={rail} label="Automation">
                        {/* "Live RCM" is the nav label only; the page and its route stay Workflows. */}
                        <SidebarNavLink
                            rail={rail}
                            to={route('workflows')}
                            icon={mdiFileTreeOutline}
                            label="Live RCM"
                        />
                        <SidebarNavLink
                            rail={rail}
                            to={route('configuration')}
                            icon={mdiRobotOutline}
                            label="Workers"
                        />
                        <SidebarNavLink rail={rail} to={route('coders')} icon={mdiCodeBraces} label="Coders" />
                    </SidebarNavGroup>
                    <SidebarNavGroup rail={rail}>
                        <SidebarNavLink
                            rail={rail}
                            to={route('file-cabinet')}
                            icon={mdiFileCabinet}
                            label="File Cabinet"
                        />
                    </SidebarNavGroup>
                </SidebarNav>
                <S.Spacer />
                <SidebarNavFooter>
                    <SidebarNavLink
                        rail={rail}
                        to={route('settings/overview')}
                        icon={mdiCogOutline}
                        label="Settings"
                        active={!!isSettingsRoute}
                    />
                </SidebarNavFooter>
                {!rail && (
                    <S.ResourceLinks>
                        <S.ResourceLink
                            href="https://developers.enter.health/docs/welcome-to-enter"
                            target="_blank"
                            rel="noopener noreferrer">
                            <S.ResourceLinkText>Developer Docs</S.ResourceLinkText>
                            <S.ResourceLinkIcon path={mdiOpenInNew} size={0.65} />
                        </S.ResourceLink>
                        <S.ResourceDivider />
                        <S.ResourceLink
                            href="https://www.enter.health/university"
                            target="_blank"
                            rel="noopener noreferrer">
                            <S.ResourceLinkText>University</S.ResourceLinkText>
                            <S.ResourceLinkIcon path={mdiOpenInNew} size={0.65} />
                        </S.ResourceLink>
                        <S.PasswordRow>
                            <S.PasswordLabel>University Password:</S.PasswordLabel>
                            <S.PasswordValueRow>
                                <S.PasswordValue
                                    clickable={showUniversityPassword}
                                    role="button"
                                    tabIndex={0}
                                    title={showUniversityPassword ? 'Copy password' : 'Show password to copy'}
                                    onClick={() => {
                                        if (!showUniversityPassword) return;
                                        copyUniversityPassword();
                                    }}
                                    onKeyDown={(e): void => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            if (!showUniversityPassword) return;
                                            copyUniversityPassword();
                                        }
                                    }}>
                                    {showUniversityPassword ? universityPassword : '••••••••••'}
                                </S.PasswordValue>
                                <S.PasswordToggle
                                    role="button"
                                    tabIndex={0}
                                    aria-label={showUniversityPassword ? 'Hide password' : 'Show password'}
                                    onClick={() => setShowUniversityPassword(prev => !prev)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setShowUniversityPassword(prev => !prev);
                                        }
                                    }}>
                                    <S.PasswordToggleIcon
                                        path={showUniversityPassword ? mdiEyeOffOutline : mdiEyeOutline}
                                        size={0.75}
                                    />
                                </S.PasswordToggle>
                                {copied && <S.CopiedText>Copied</S.CopiedText>}
                            </S.PasswordValueRow>
                        </S.PasswordRow>
                    </S.ResourceLinks>
                )}
                {demoModeEnabled && (
                    <S.DemoModeIndicator rail={rail}>
                        <S.DemoModeText>{rail ? 'Demo' : 'Demo Mode'}</S.DemoModeText>
                    </S.DemoModeIndicator>
                )}
            </S.SideBar>
        );
    }),
);
